import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  CircularProgress,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  PregnantWoman,
  Timeline,
  Warning,
  BabyChangingStation,
  EmojiEvents,
  WavingHand,
  TrendingUp,
  CalendarToday,
  Chat,
  Message,
  MedicalServices,
  Assignment,
  Search,
} from '@mui/icons-material';
import MobileAppPromotion from '../../components/common/MobileAppPromotion';
import ElsaChatbot from '../../components/common/ElsaChatbot';
import AppointmentBooking from '../../components/appointments/AppointmentBooking';
import KickCounter from '../../components/common/KickCounter';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchUserSubscription } from '../../store/slices/subscriptionSlice';
import { hybridPaymentService, PaymentMethod } from '../../services/hybridPaymentService';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';

const PatientDashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentSubscription, loading: subscriptionLoading } = useSelector((state: RootState) => state.subscription);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [kickDialogOpen, setKickDialogOpen] = useState(false);
  const [kickAnalysisResult, setKickAnalysisResult] = useState<any>(null);
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [emergencyData, setEmergencyData] = useState({
    type: 'pain',
    description: '',
  });
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageData, setMessageData] = useState({
    content: '',
  });
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [kickSessions, setKickSessions] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [medicalRecords, setMedicalRecords] = useState<any[]>([]);

  useEffect(() => {
    if (user) {
      dispatch(fetchUserSubscription() as any);
      loadUserData();
    }
  }, [user, dispatch]);

  const loadUserData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // Load user profile
      const userProfileQuery = query(
        collection(db!, 'userProfiles'),
        where('userId', '==', user.id)
      );
      const userProfileSnapshot = await getDocs(userProfileQuery);
      if (!userProfileSnapshot.empty) {
        setUserProfile(userProfileSnapshot.docs[0].data());
      }

      // Load kick sessions (recent 10)
      const kickSessionsQuery = query(
        collection(db!, 'kickSessions'),
        where('patientId', '==', user.id),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const kickSessionsSnapshot = await getDocs(kickSessionsQuery);
      setKickSessions(kickSessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Load appointments (upcoming)
      const appointmentsQuery = query(
        collection(db!, 'appointments'),
        where('patientId', '==', user.id),
        orderBy('scheduledTime', 'asc')
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      setAppointments(appointmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Load medical records (recent)
      const medicalRecordsQuery = query(
        collection(db!, 'medicalRecords'),
        where('patientId', '==', user.id),
        orderBy('uploadedAt', 'desc'),
        limit(5)
      );
      const medicalRecordsSnapshot = await getDocs(medicalRecordsQuery);
      setMedicalRecords(medicalRecordsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  interface Activity {
    time: string;
    title: string;
    subtitle: string;
    icon: any;
    color: string;
    timestamp: Date;
  }

  // Use real data from Firebase
  const patientData = {
    name: userProfile?.name || 'Mama',
    pregnancyWeek: userProfile?.pregnancyWeek || 0,
    dueDate: userProfile?.dueDate || '',
  };

  // Calculate today's kicks from real data
  const today = new Date().toDateString();
  const todaySessions = kickSessions.filter(session => {
    const sessionDate = new Date(session.createdAt?.toDate?.() || session.createdAt).toDateString();
    return sessionDate === today;
  });
  const todayKicks = todaySessions.reduce((sum, session) => sum + (session.kickCount || 0), 0);
  const goals = [{ title: 'Daily Kick Count', current: todayKicks, target: 10, unit: 'kicks', isCompleted: todayKicks >= 10 }];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getComparisonText = (todayKicks: number) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKicks = 6; // Mock data
    const difference = todayKicks - yesterdayKicks;
    if (difference > 0) return `+${difference} vs yesterday`;
    if (difference < 0) return `${difference} vs yesterday`;
    return 'Same as yesterday';
  };

  const getRecentActivities = (): Activity[] => {
    const activities: Activity[] = [];

    // Add kick sessions from Firebase
    kickSessions.slice(0, 3).forEach((session) => {
      const sessionDate = session.createdAt?.toDate?.() || new Date(session.createdAt);
      activities.push({
        time: sessionDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        title: 'Kick session recorded',
        subtitle: `${session.kickCount || 0} kicks in ${session.duration || 0} minutes`,
        icon: BabyChangingStation,
        color: '#FFB6C1',
        timestamp: sessionDate,
      });
    });

    // Add upcoming appointments
    appointments.slice(0, 2).forEach((appointment) => {
      const appointmentDate = new Date(appointment.scheduledTime);
      activities.push({
        time: appointmentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        title: 'Upcoming appointment',
        subtitle: `${appointment.type} appointment on ${appointmentDate.toLocaleDateString()}`,
        icon: CalendarToday,
        color: '#4A90E2',
        timestamp: appointmentDate,
      });
    });

    // Add medical records
    medicalRecords.slice(0, 2).forEach((record) => {
      const recordDate = record.uploadedAt?.toDate?.() || new Date(record.uploadedAt);
      activities.push({
        time: recordDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        title: 'Medical record uploaded',
        subtitle: `${record.category}: ${record.fileName}`,
        icon: MedicalServices,
        color: '#7B68EE',
        timestamp: recordDate,
      });
    });

    // Add goal progress
    goals.forEach(goal => {
      activities.push({
        time: 'Today',
        title: `${goal.title} progress`,
        subtitle: `${goal.current} / ${goal.target} ${goal.unit}`,
        icon: EmojiEvents,
        color: '#48BB78',
        timestamp: new Date(),
      });
    });

    // Filter activities based on search term
    const filteredActivities = searchTerm
      ? activities.filter(activity =>
          activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          activity.subtitle.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : activities;

    return filteredActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()).slice(0, 5);
  };

  const handleKickSessionComplete = (session: any, assessment: any) => {
    console.log('Kick session completed:', session, assessment);
    setKickAnalysisResult({ session, assessment });
    // Reload user data to show updated kick sessions
    loadUserData();
  };

  const handleEmergencyAlert = () => {
    console.log('Sending emergency alert:', emergencyData);
    setEmergencyDialogOpen(false);
    setEmergencyData({ type: 'pain', description: '' });
  };

  const handleSubscribe = async () => {
    if (!user?.email) return;

    const reference = `sub_${Date.now()}`;

    try {
      await hybridPaymentService.initiatePaystackPayment({
        amount: 30000, // KES 300 * 100
        email: user.email,
        reference,
        currency: 'KES',
        callback: async (response: any) => {
          if (response.status === 'success') {
            await hybridPaymentService.verifyPaystackPayment(reference);
            await hybridPaymentService.createSubscription({
              email: user.email,
              amount: 300,
              planName: 'premium',
              method: PaymentMethod.PAYSTACK,
            });
            dispatch(fetchUserSubscription() as any);
            setSubscriptionDialogOpen(false);
            // Show success message
          }
        },
        onClose: () => {
          console.log('Payment modal closed');
        },
      });
    } catch (error) {
      console.error('Subscription failed:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ flex: 1, backgroundColor: '#f8f9fa', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Box textAlign="center">
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading your dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, backgroundColor: '#f8f9fa' }}>
      {/* Top App Bar */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {getGreeting()}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 0.5 }}>
              Hello Mama
            </Typography>
          </Box>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: 'primary.container',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/patient/profile')}
          >
            <WavingHand sx={{ color: 'primary.main' }} />
          </Avatar>
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 2 }}>
        {/* Summary Card */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #0A8D92 0%, #4FB3B7 100%)',
            color: 'white',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            mb: 3
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {todayKicks}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                  kicks today
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.8 }}>
                  {getComparisonText(todayKicks)}
                </Typography>
              </Box>
              <Box>
                <CircularProgress
                  variant="determinate"
                  value={(todayKicks / 10) * 100}
                  size={80}
                  thickness={6}
                  sx={{
                    color: 'rgba(255,255,255,0.3)',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {Math.round((todayKicks / 10) * 100)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Premium Banner */}
        {!currentSubscription && !subscriptionLoading && (
          <Card
            sx={{
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: 'white',
              borderRadius: 3,
              mb: 3,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Upgrade to Premium
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                    Unlock unlimited kick monitoring, AI insights, personalized meal plans, and more!
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                      }
                    }}
                    onClick={() => setSubscriptionDialogOpen(true)}
                  >
                    Upgrade Now - KES 300/month
                  </Button>
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', opacity: 0.8 }}>
                    ⭐
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        )}

        {/* Quick Stats */}
        <Box display="flex" gap={2} mb={3}>
          <Card sx={{ flex: 1, p: 2, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <PregnantWoman sx={{ color: 'primary.main', fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {patientData.pregnancyWeek || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Week
                </Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ flex: 1, p: 2, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Timeline sx={{ color: 'secondary.main', fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  {kickSessions.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Sessions
                </Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ flex: 1, p: 2, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <EmojiEvents sx={{ color: 'tertiary.main', fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'tertiary.main' }}>
                  {goals.filter(g => g.isCompleted).length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Goals
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Quick Actions */}
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
          Quick Actions
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={2} mb={4}>
          <Card sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2 }} onClick={() => setKickDialogOpen(true)}>
            <BabyChangingStation sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Record Kick
            </Typography>
          </Card>
          <Card sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2 }} onClick={() => setEmergencyDialogOpen(true)}>
            <Warning sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Emergency
            </Typography>
          </Card>
          <Card sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2 }} onClick={() => setChatbotOpen(true)}>
            <Chat sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Chat Elsa
            </Typography>
          </Card>
          <Card sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2 }} onClick={() => navigate('/patient/timeline')}>
            <Timeline sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Timeline
            </Typography>
          </Card>
          <Card sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2 }} onClick={() => navigate('/patient/tasks')}>
            <Assignment sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Tasks
            </Typography>
          </Card>
          <Card sx={{ p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2 }} onClick={() => navigate('/patient/appointments')}>
            <CalendarToday sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Appointments
            </Typography>
          </Card>
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search activities, sessions, goals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        {/* Recent Activity */}
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
           Recent Activity
         </Typography>

        {getRecentActivities().length > 0 ? (
          getRecentActivities().map((activity, index) => (
            <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
              <Box sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: `${activity.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <activity.icon sx={{ color: activity.color, fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {activity.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {activity.subtitle}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {activity.time}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))
        ) : (
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography sx={{ color: 'text.secondary' }}>
              Start tracking to see your progress here
            </Typography>
          </Card>
        )}
      </Box>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(135deg, #FFB6C1 0%, #FF69B4 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)',
          }
        }}
        onClick={() => setKickDialogOpen(true)}
      >
        <BabyChangingStation />
      </Fab>

      {/* Advanced Fetal Health Kick Counter */}
      <KickCounter
        open={kickDialogOpen}
        onClose={() => setKickDialogOpen(false)}
        onSessionComplete={handleKickSessionComplete}
      />

      {/* Emergency Alert Dialog */}
      <Dialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
          Emergency Alert
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will send an immediate alert to your healthcare provider. Use only for genuine emergencies.
          </Alert>
          <FormControl fullWidth margin="normal">
            <InputLabel>Emergency Type</InputLabel>
            <Select
              value={emergencyData.type}
              onChange={(e) => setEmergencyData({ ...emergencyData, type: e.target.value })}
              label="Emergency Type"
            >
              <MenuItem value="pain">Severe Pain</MenuItem>
              <MenuItem value="bleeding">Bleeding</MenuItem>
              <MenuItem value="reduced_movement">Reduced Fetal Movement</MenuItem>
              <MenuItem value="other">Other Emergency</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            value={emergencyData.description}
            onChange={(e) => setEmergencyData({ ...emergencyData, description: e.target.value })}
            margin="normal"
            required
            placeholder="Please describe your emergency situation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmergencyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEmergencyAlert} variant="contained" color="error">
            Send Emergency Alert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Elsa Chatbot */}
      <ElsaChatbot
        open={chatbotOpen}
        onClose={() => setChatbotOpen(false)}
      />

      {/* Premium Subscription Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Upgrade to Premium
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
              Premium Plan - KES 300/month
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                'Unlimited kick monitoring trends',
                'Premium pregnancy insights',
                'Personalized meal plans by AI',
                'Unlimited Ask-Elsa AI questions',
                'Downloadable pregnancy reports',
                'Save emergency contacts',
                'Mood tracker + mental health tools',
                'Vaccination reminders',
                'Weekly doctor-reviewed tips',
              ].map((feature, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ color: 'primary.main' }}>✓</Typography>
                  <Typography variant="body2">{feature}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cancel anytime. 7-day free trial available for new subscribers.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubscribe}
            disabled={subscriptionLoading}
          >
            {subscriptionLoading ? <CircularProgress size={20} /> : 'Subscribe Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDashboard;